import type * as React from "react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Badge } from "../../primitives/badge";
import { useI18n } from "../../lib/i18n";
import type { ActionDrawerStatus } from "../action-drawer/types";

/**
 * ActionStatusBadge — single source of truth for the
 * `proposed | editing | confirming | executing | executed | error`
 * → label + Badge variant mapping. Previously inlined in 3 places
 * (FocusedActionView, ActionDrawerHeader, ActionFormSurface) with
 * subtle divergence in labels ("Proposta" vs "In attesa") — unified
 * here so the user sees identical wording everywhere.
 */
const STATUS_LABEL: Record<ActionDrawerStatus, MessageDescriptor> = {
  proposed: msg`Proposta`,
  editing: msg`In modifica`,
  confirming: msg`Da confermare`,
  executing: msg`In esecuzione`,
  executed: msg`Eseguita`,
  error: msg`Errore`,
};

type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>["variant"]>;

const STATUS_VARIANT: Record<ActionDrawerStatus, BadgeVariant> = {
  proposed: "ghost",
  editing: "accent",
  confirming: "default",
  executing: "secondary",
  executed: "success",
  error: "destructive",
};

export interface ActionStatusBadgeProps {
  readonly status: ActionDrawerStatus;
  readonly className?: string;
}

export const ActionStatusBadge: React.FC<ActionStatusBadgeProps> = ({
  status,
  className,
}) => {
  const { i18n } = useI18n();
  return (
    <Badge variant={STATUS_VARIANT[status]} className={className}>
      {i18n._(STATUS_LABEL[status])}
    </Badge>
  );
};
ActionStatusBadge.displayName = "ActionStatusBadge";

/**
 * Imperative accessor — returns the raw ``MessageDescriptor`` so callers
 * can resolve it at the appropriate render-time via ``i18n._()``.
 */
export const actionStatusLabel = (
  status: ActionDrawerStatus,
): MessageDescriptor => STATUS_LABEL[status];

export const actionStatusVariant = (status: ActionDrawerStatus): BadgeVariant =>
  STATUS_VARIANT[status];
